import React, { useEffect, useMemo, useRef } from 'react';
import type { Feature } from '@fulcrumapp/fulcrum-core';
import { Box, type SxProps, type Theme } from '@mui/material';
import ArcGisMapView from '../../shared/map/arc-gis-map-view';
import Basemap from '../../shared/map/plugins/basemap';
import LayerPickerPlugin from '../../shared/map/plugins/layer-picker-plugin';
import LayerSettingsReferenceHandler from '../../shared/map/plugins/utils/layer-settings-reference-handler';
import type { LayerSettingsHandler } from '../../shared/map/plugins/utils/layer-settings-reference-handler';
import type { MapPlugin } from '../../shared/map/arcgis-types';
import FormRecordsPlugin from './plugins/form-records-plugin';
import PhotoMarkerPlugin from './plugins/photo-marker-plugin';
import { useApp } from '../../hooks/use-app';
import { defaultExtents, type Extents } from '../../types/geo';
import type { Metadata } from './display/media-displayer';
import { hasFeatureCoordinates } from './feature-coordinates';

const PHOTO_GPS_ZOOM = 20;
const FEATURE_FALLBACK_ZOOM = 15;

const containerStyles: SxProps<Theme> = (theme) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    borderLeft: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
});

interface Props {
  className: string;
  feature?: Feature;
  metadata?: Metadata;
  visible?: boolean;
}

const PhotoArcGisMapView = ({
  className, feature, metadata, visible,
}: Props): React.ReactElement => {
  const app = useApp();
  const { layerSettings, api: { token } } = app;
  const googleApiKey = app.globals.config.google_maps.api_key;

  const extents: Extents = useMemo(() => {
    const { latitude, longitude } = metadata ?? {};
    if (latitude != null && longitude != null && latitude !== 0 && longitude !== 0) {
      return { latitude, longitude, zoom: PHOTO_GPS_ZOOM };
    }
    const coords = hasFeatureCoordinates(feature);
    if (coords) {
      return { latitude: coords.latitude, longitude: coords.longitude, zoom: FEATURE_FALLBACK_ZOOM };
    }
    return defaultExtents;
  }, [metadata, feature]);

  const handlerRef = useRef<LayerSettingsHandler | null>(null);
  handlerRef.current ??= LayerSettingsReferenceHandler(layerSettings);

  useEffect(() => {
    handlerRef.current?.updateInstance(layerSettings);
  }, [layerSettings]);

  useEffect(() => () => {
    handlerRef.current?.destroy();
  }, []);

  const basePluginsRef = useRef<MapPlugin[] | null>(null);
  basePluginsRef.current ??= [
    Basemap(handlerRef.current, googleApiKey),
    LayerPickerPlugin('media-viewer'),
    FormRecordsPlugin(layerSettings.dataLayer, token),
  ];

  const photoMarkerPlugin = useMemo(
    () => PhotoMarkerPlugin(feature, metadata),
    [feature, metadata],
  );

  const plugins = useMemo(
    () => [...basePluginsRef.current!, photoMarkerPlugin],
    [photoMarkerPlugin],
  );

  return (
    <Box className={className} sx={containerStyles}>
      {visible !== false && (
        <ArcGisMapView
          extents={extents}
          plugins={plugins}
          parentView="media-viewer"
        />
      )}
    </Box>
  );
};

export default PhotoArcGisMapView;
